import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  trade: { label: "建议交易", color: "bg-green-100 border-green-300" },
  reduce: { label: "建议减频", color: "bg-yellow-100 border-yellow-300" },
  skip: { label: "建议跳过", color: "bg-red-100 border-red-300" },
};

const RISK_COLORS: Record<string, string> = {
  low: "text-green-600",
  medium: "text-yellow-600",
  high: "text-red-600",
};

// A类低风险参数白名单
const LOW_RISK_PARAMS = ["maxDailyRounds", "tailProtectMinutes", "s1ObserveWindowSec", "s2ObserveWindowSec"];

export default function DailyAdvicePanel({ monitorId, stockName }: { monitorId: number; stockName?: string }) {
  const [show, setShow] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; type: "safe" | "all"; params: Record<string, any> }>({ open: false, type: "safe", params: {} });
  const [applying, setApplying] = useState(false);

  const q = trpc.strategyTemplate.dailyAdvice.useQuery({ monitorId }, { enabled: show, staleTime: 30 * 60 * 1000 });
  const applyMutation = trpc.backtest.applyParams.useMutation();
  const revertMutation = trpc.strategyTemplate.revertParams.useMutation();
  const [reverting, setReverting] = useState(false);
  const handleRevert = async () => {
    if (!confirm("确认撤销上次AI参数调整？")) return;
    setReverting(true);
    try {
      const r = await revertMutation.mutateAsync({ monitorId });
      toast.success(`已撤销 ${r.restoredParams} 个参数`);
      q.refetch();
    } catch (e: any) {
      toast.error("撤销失败: " + (e.message || "未知错误"));
    } finally { setReverting(false); }
  };

  const data = q.data;
  const adv = data?.advice;
  const scoring = data?.scoring;
  const metrics = data?.metrics;

  // 实际应用参数到agent_monitors
  const doApply = async (params: Record<string, any>) => {
    setApplying(true);
    try {
      const result = await applyMutation.mutateAsync({ monitorId, params });
      toast.success(`已应用 ${result.paramCount} 个参数`);
      setConfirmDialog({ open: false, type: "safe", params: {} });
      q.refetch();
    } catch (e: any) {
      toast.error("应用失败: " + (e.message || "未知错误"));
    } finally {
      setApplying(false);
    }
  };

  // 采纳安全参数（仅A类低风险）
  const handleAdoptSafe = () => {
    if (!adv?.paramAdjustments) return;
    const safeParams: Record<string, any> = {};
    for (const [k, v] of Object.entries(adv.paramAdjustments)) {
      if (LOW_RISK_PARAMS.includes(k)) safeParams[k] = v;
    }
    if (Object.keys(safeParams).length === 0) {
      toast.info("无低风险参数可应用");
      return;
    }
    doApply(safeParams);
  };

  // 全部采纳（含B/C类，需二级确认）
  const handleAdoptAll = () => {
    if (!adv?.paramAdjustments) return;
    const allParams = { ...adv.paramAdjustments };
    const hasHighRisk = Object.entries(adv.paramRiskLevels || {}).some(([_, v]) => v === "high");
    if (hasHighRisk || Object.keys(allParams).length > 2) {
      // 二级确认弹窗
      setConfirmDialog({ open: true, type: "all", params: allParams });
    } else {
      doApply(allParams);
    }
  };

  // 单项确认应用
  const handleApplySingle = (key: string, value: any) => {
    doApply({ [key]: value });
  };

  if (!show) return (
    <button
      onClick={() => setShow(true)}
      className="text-xs text-blue-500 hover:text-blue-700 px-2 py-1 rounded border border-blue-200/50 hover:border-blue-400/50 transition-colors"
    >
      🤖 {stockName ? stockName + " " : ""}操作建议
    </button>
  );

  const actionInfo = ACTION_LABELS[scoring?.action || "skip"] || ACTION_LABELS.skip;

  // 极端亏损预演（C类参数含止损时展示）
  const calcWorstCase = (params: Record<string, any>) => {
    const stopLoss = params.s1StopLossRisePct || params.s2StopLossDropPct;
    const maxRounds = params.maxDailyRounds || 10;
    const maxConsec = params.maxConsecutiveStopLoss || 3;
    if (!stopLoss) return null;
    const holding = data?.stockInfo?.holding || 1000;
    const price = data?.stockInfo?.lastPrice || 10;
    const worstLoss = (stopLoss / 100) * price * holding * maxConsec;
    return { stopLoss, maxConsec, worstLoss: worstLoss.toFixed(0) };
  };

  return (
    <div className="mt-3 border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="bg-blue-50 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {metrics && (
            <span className={`text-xs px-2 py-0.5 rounded ${(metrics.mktScore ?? 50) >= 50 ? "bg-green-200" : "bg-red-200"}`}>
              大盘: {metrics.mktScore ?? "N/A"}
            </span>
          )}
          <span className="font-medium text-sm">AI操作建议 {stockName ? "- " + stockName : ""}</span>
        </div>
        <button onClick={() => setShow(false)} className="text-gray-400 text-xs">收起</button>
      </div>

      <div className="p-4 space-y-3">
        {q.isLoading && <div className="text-sm text-gray-500">AI 分析中...</div>}
        {q.error && (
          <div className="text-sm text-red-500">
            分析失败
            <button onClick={() => q.refetch()} className="ml-2 text-blue-500 underline">重试</button>
          </div>
        )}

        {adv && scoring && (
          <>
            {/* Scoring breakdown */}
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-semibold">评分: {scoring.finalScore}</span>
                  <span className="text-xs text-gray-500 ml-2">
                    (规则{scoring.ruleBaseScore} {scoring.llmAdjust >= 0 ? "+" : ""}{scoring.llmAdjust} AI修正)
                  </span>
                </div>
                <span className={`text-xs px-2 py-1 rounded border ${actionInfo.color}`}>
                  {actionInfo.label}
                </span>
              </div>
              {metrics && (
                <div className="flex gap-3 mt-2 text-xs text-gray-500 flex-wrap">
                  <span>振幅: {metrics.wAvgAmp}%</span>
                  <span>NetEdge: {metrics.netEdge}%</span>
                  <span>结构: {metrics.structScore}</span>
                  <span>胜率: {metrics.winRate}%</span>
                  {metrics.holdingPnlPct !== 0 && <span>浮盈: {metrics.holdingPnlPct}%</span>}
                </div>
              )}
            </div>

            {/* AI Analysis */}
            {(adv.marketAnalysis || adv.stockAnalysis) && (
              <div className="text-xs space-y-1">
                {adv.marketAnalysis && <p><span className="text-gray-400">大盘: </span>{adv.marketAnalysis}</p>}
                {adv.stockAnalysis && <p><span className="text-gray-400">个股: </span>{adv.stockAnalysis}</p>}
                {adv.directionBias && <p><span className="text-gray-400">方向: </span>{adv.directionBias}</p>}
                {adv.bestTimeWindow && <p><span className="text-gray-400">时段: </span>{adv.bestTimeWindow}</p>}
              </div>
            )}

            {/* Risk warnings */}
            {adv.riskWarnings?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="font-medium text-xs text-amber-800 mb-1">风险提醒</div>
                <ul className="text-xs text-amber-700 space-y-0.5">
                  {adv.riskWarnings.map((w: string, i: number) => <li key={i}>• {w}</li>)}
                </ul>
              </div>
            )}

            {/* Template switch */}
            {adv.templateSwitch?.recommended && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-2 text-xs">
                <span className="text-purple-700">建议切换模板: {adv.templateSwitch.targetTemplate}</span>
                {adv.templateSwitch.reason && <span className="text-gray-500 ml-1">({adv.templateSwitch.reason})</span>}
              </div>
            )}

            {/* Parameter adjustments with REAL apply buttons */}
            {adv.paramAdjustments && Object.keys(adv.paramAdjustments).length > 0 && (
              <div className="border rounded-lg">
                <div className="bg-gray-50 px-3 py-2 flex items-center justify-between">
                  <span className="font-medium text-xs">
                    参数建议 ({scoring.action === "reduce" ? "仅低风险" : "完整"})
                  </span>
                  {adv.adjustReason && <span className="text-xs text-gray-400">{adv.adjustReason}</span>}
                </div>
                <div className="divide-y">
                  {Object.entries(adv.paramAdjustments).map(([k, v]) => {
                    const riskLevel = adv.paramRiskLevels?.[k] || "low";
                    const delta = adv.paramDeltas?.[k];
                    return (
                      <div key={k} className="flex items-center justify-between px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs ${RISK_COLORS[riskLevel]}`}>{riskLevel === "high" ? "⚠️" : riskLevel === "medium" ? "●" : "○"}</span>
                          <span className="text-xs text-gray-600">{k}</span>
                          <span className="text-xs font-mono text-blue-600">{String(v)}</span>
                          {delta && <span className="text-xs text-gray-400">({(delta as number) > 0 ? "+" : ""}{String(delta)})</span>}
                        </div>
                        <button
                          onClick={() => handleApplySingle(k, v)}
                          disabled={applying || (metrics?.mktScore != null && metrics.mktScore < 30)}
                          className="text-xs bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          确认
                        </button>
                      </div>
                    );
                  })}
                </div>
                <div className="bg-gray-50 px-3 py-2 flex gap-2">
                  <button
                    onClick={handleAdoptSafe}
                    disabled={applying || (metrics?.mktScore != null && metrics.mktScore < 30)}
                    className="text-xs bg-green-500 text-white px-4 py-1.5 rounded hover:bg-green-600 disabled:opacity-40"
                  >
                    {applying ? "应用中..." : "采纳安全参数"}
                  </button>
                  {Object.values(adv.paramRiskLevels || {}).some((v: any) => v === "high" || v === "medium") && (
                    <button
                      onClick={handleAdoptAll}
                      disabled={applying || (metrics?.mktScore != null && metrics.mktScore < 30)}
                      className="text-xs bg-orange-500 text-white px-4 py-1.5 rounded hover:bg-orange-600 disabled:opacity-40"
                    >
                      全部采纳 ⚠️
                    </button>
                  )}
                  <button onClick={() => setShow(false)} className="text-xs bg-gray-200 text-gray-600 px-4 py-1.5 rounded hover:bg-gray-300">
                    忽略
                  </button>
                </div>
                {/* 大盘<30时置灰提示 */}
                {metrics?.mktScore != null && metrics.mktScore < 30 && (
                  <div className="px-3 py-1.5 text-xs text-red-500 bg-red-50">
                    ⚠️ 大盘评分{metrics.mktScore}&lt;30，确认应用按钮已锁定
                  </div>
                )}
              </div>
            )}

            {/* Delta warnings */}
            {adv.deltaWarnings?.length > 0 && (
              <div className="text-xs text-gray-400">
                {adv.deltaWarnings.map((w: string, i: number) => <span key={i} className="mr-2">[{w}]</span>)}
              </div>
            )}

            {/* Fallback for raw text */}
            {adv.rawText && !adv.recommendedAction && (
              <div className="text-xs text-gray-600 whitespace-pre-wrap bg-gray-50 rounded p-3">{adv.rawText}</div>
            )}

            <div className="flex justify-end gap-3">
              <button onClick={handleRevert} disabled={reverting} className="text-xs text-orange-400 hover:text-orange-600 disabled:opacity-40">{reverting ? "撤销中..." : "↩️ 撤销上次AI调参"}</button>
              <button onClick={() => q.refetch()} className="text-xs text-gray-400 hover:text-blue-500">↻ 重新分析</button>
            </div>
          </>
        )}
      </div>

      {/* 二级确认弹窗（全部采纳含C类高风险参数时） */}
      {confirmDialog.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-base mb-3">⚠️ 确认应用全部参数</h3>

            <div className="space-y-2 mb-4">
              {Object.entries(confirmDialog.params).map(([k, v]) => {
                const riskLevel = adv?.paramRiskLevels?.[k] || "low";
                const delta = adv?.paramDeltas?.[k];
                return (
                  <div key={k} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className={RISK_COLORS[riskLevel]}>{riskLevel === "high" ? "⚠️" : "●"}</span>
                      <span>{k}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono">{String(v)}</span>
                      {delta && (
                        <span className={`text-xs ${(delta as number) > 0 ? "text-red-500" : "text-green-500"}`}>
                          ({(delta as number) > 0 ? "+" : ""}{String(delta)}
                          {adv?.paramAdjustments?.[k] != null && typeof delta === "number" && typeof adv.paramAdjustments[k] === "number"
                            ? ` / ${Math.abs(Math.round(delta / (adv.paramAdjustments[k] as number - delta) * 100))}%`
                            : ""})
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 极端亏损预演 */}
            {(() => {
              const worst = calcWorstCase(confirmDialog.params);
              if (!worst) return null;
              return (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <div className="font-medium text-xs text-red-800 mb-1">极端亏损预演</div>
                  <div className="text-xs text-red-700">
                    若触发最大连续止损({worst.maxConsec}次 × {worst.stopLoss}%)，
                    <span className="font-bold text-red-900">单日最大亏损约 ¥{worst.worstLoss}</span>
                  </div>
                </div>
              );
            })()}

            <div className="flex gap-3">
              <button
                onClick={() => doApply(confirmDialog.params)}
                disabled={applying}
                className="flex-1 bg-orange-500 text-white py-2 rounded-lg hover:bg-orange-600 text-sm font-medium disabled:opacity-40"
              >
                {applying ? "应用中..." : "确认全部应用"}
              </button>
              <button
                onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
                className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 text-sm"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
